import {
  Badge,
  Status,
  Text,
  TextSize,
  TextWeight,
  additionalPalette,
  makeStyles
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { AnalyticsFeatureFlagProvider } from '../../../../contexts/AnalyticsFeatureFlagContext'
import { stringToStatusMap } from '../../../../controls/ProjectListItem/ProjectListItem.utils'
import { Survey, SurveyState } from '../../../../data/gql-gen/dashboard/graphql'
import { SurveyIdProvider } from '../../../../hooks/SurveyIdContext'
import { formatThousandsWithCommas } from '../../../../utils/HelperFunctions'
import { parseMarketName } from '../../../../utils/marketFlags'
import { MarketQuotaBreakdown } from './MarketQuotaBreakdown'

// Overall progress bar turns green once a market is essentially on target.
const NEAR_COMPLETE_PCT = 90

const useStyles = makeStyles()({
  card: {
    border: '1px solid #E5EAF5',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12
  },
  nameRow: { display: 'flex', alignItems: 'center', gap: 10 },
  count: { color: '#42526E', whiteSpace: 'nowrap' },
  track: {
    height: 8,
    background: '#E9ECF3',
    borderRadius: 0,
    marginTop: 12,
    overflow: 'hidden'
  },
  fill: {
    height: '100%',
    borderRadius: 0,
    background: additionalPalette.liveBlue.main
  },
  fillComplete: { background: '#2F8A5B' },
  // reset so a native (accessible) <button> can be styled as a link
  toggle: {
    background: 'none',
    border: 'none',
    padding: 0,
    marginTop: 14,
    cursor: 'pointer',
    color: 'var(--primary)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6
  },
  breakdown: { marginTop: 8 }
})

export const MarketFieldworkCard = ({
  survey,
  displayName,
  collected,
  target
}: {
  survey: Survey
  // Resolved label for the base market, whose name may be a legacy project
  // name; defaults to the market's own name (trailing "(XX)" stripped).
  displayName?: string
  collected: number
  target: number
}) => {
  const { classes, cx } = useStyles()
  const [expanded, setExpanded] = useState(false)

  const pct = target > 0 ? Math.round((collected / target) * 100) : 0

  return (
    <div className={classes.card}>
      <div className={classes.headerRow}>
        <span className={classes.nameRow}>
          <Text size={TextSize.l} weight={TextWeight.Bold}>
            {displayName ?? parseMarketName(survey.name).label}
          </Text>
          <Badge
            label={survey.state}
            status={stringToStatusMap.get(survey.state) ?? Status.DRAFT}
          />
        </span>
        <Text size={TextSize.ms}>
          <span className={classes.count}>
            {formatThousandsWithCommas(collected)} /{' '}
            {formatThousandsWithCommas(target)} ({pct}%)
          </span>
        </Text>
      </div>

      <div className={classes.track}>
        <div
          className={cx(
            classes.fill,
            pct >= NEAR_COMPLETE_PCT && classes.fillComplete
          )}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>

      {/* Quota fill only exists once this market's order is placed, so a
          still-Draft market gets no breakdown toggle. */}
      {survey.state !== SurveyState.Draft && (
        <button
          type="button"
          className={classes.toggle}
          onClick={() => setExpanded((open) => !open)}
        >
          <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
            {expanded ? '▾' : '▸'} Quota Breakdown
          </Text>
        </button>
      )}

      {expanded && (
        <div className={classes.breakdown}>
          {/* The fieldwork route is project-scoped (no :surveyId), so supply the
              market's surveyId to the per-survey analytics-flag hooks, which read
              useSurveyId(). Quota data itself is fetched per market via the prop. */}
          <SurveyIdProvider value={survey.surveyId}>
            <AnalyticsFeatureFlagProvider surveyId={survey.surveyId}>
              <MarketQuotaBreakdown surveyId={survey.surveyId} />
            </AnalyticsFeatureFlagProvider>
          </SurveyIdProvider>
        </div>
      )}
    </div>
  )
}
